import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { CAREER_ACCENT_LABELS, CAREER_ACCENTS, type CareerAccent } from '@nest-recruit/shared';
import styles from './AccentSwatches.module.css';

const STEPS: Readonly<Record<string, 1 | -1>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/**
 * The site's colour as three named swatches (Option A), the same three choices as before — a Flock token each, never a
 * typed colour. A radio group: the arrow keys move the choice, and only the chosen swatch is a Tab stop (06-qol §2).
 */
export function AccentSwatches({
  value,
  onChange,
  disabled = false,
  labelId,
}: {
  readonly value: CareerAccent;
  readonly onChange: (accent: CareerAccent) => void;
  readonly disabled?: boolean;
  readonly labelId: string;
}): ReactNode {
  const group = useRef<HTMLDivElement>(null);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = STEPS[event.key];
    if (step === undefined || disabled) return;
    event.preventDefault();
    const next = CAREER_ACCENTS[(index + step + CAREER_ACCENTS.length) % CAREER_ACCENTS.length];
    if (!next) return;
    onChange(next);
    group.current?.querySelectorAll<HTMLButtonElement>('button')[CAREER_ACCENTS.indexOf(next)]?.focus();
  }

  return (
    <div ref={group} className={styles.group} role="radiogroup" aria-labelledby={labelId} aria-disabled={disabled || undefined}>
      {CAREER_ACCENTS.map((accent, index) => {
        const isChecked = accent === value;
        return (
          <button
            key={accent}
            type="button"
            role="radio"
            aria-checked={isChecked}
            tabIndex={isChecked ? 0 : -1}
            className={styles.swatch}
            data-accent={accent}
            disabled={disabled}
            onClick={() => onChange(accent)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            <span className={styles.dot} aria-hidden />
            {CAREER_ACCENT_LABELS[accent]}
          </button>
        );
      })}
    </div>
  );
}
